import { RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'

import { BillingTokenUsageTable } from '@/components/billing/billing-token-usage-table.organism'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useEnrichedUsageData } from '@/hooks/billing/use-enriched-usage-data.hook'

import { useBalanceSettingsUsage } from './use-balance-settings-usage.hook'

interface UsageErrorAlertProps {
  error: string
  onRetry: () => void
}

const UsageErrorAlert: React.FC<UsageErrorAlertProps> = ({ error, onRetry }): ReactNode => (
  <Alert variant="destructive" className="billing-usage-error mb-4">
    <AlertTitle>Failed to load usage data</AlertTitle>
    <AlertDescription className="flex items-center justify-between">
      <span>{error}</span>
      <Button
        variant="outline"
        size="sm"
        onClick={onRetry}
        className="billing-usage-retry-button ml-4"
      >
        <RefreshCw className="mr-2 h-4 w-4" />
        Retry
      </Button>
    </AlertDescription>
  </Alert>
)

export const BalanceTokenUsageSection: React.FC = (): ReactNode => {
  const usage = useBalanceSettingsUsage()
  const enrichedData = useEnrichedUsageData(usage.paginatedUsageData)

  return (
    <section className="billing-token-usage-section">
      <div className="billing-token-usage-header mb-4">
        <h3 className="text-lg font-medium">Token Usage</h3>
        <p className="text-sm text-muted-foreground">
          Track your AI usage including LLM calls, tool executions, and API requests.
        </p>
      </div>
      {usage.error && <UsageErrorAlert error={usage.error} onRetry={usage.refetch} />}
      <BillingTokenUsageTable
        data={enrichedData}
        pagination={{
          page: usage.usagePagination.page,
          pageSize: usage.usagePagination.pageSize,
          totalCount: usage.processedTotalCount,
        }}
        searchValue={usage.usageSearchValue}
        onSearchChange={usage.setUsageSearchValue}
        filterValueList={usage.usageFilterValueList}
        onFilterChange={usage.setUsageFilterValueList}
        sortBy={usage.usageSortBy}
        sortOrder={usage.usageSortOrder}
        onSortChange={usage.handleUsageSortChange}
        onPageChange={usage.handleUsagePageChange}
        onPageSizeChange={usage.handleUsagePageSizeChange}
        isLoading={usage.isLoading}
      />
    </section>
  )
}

BalanceTokenUsageSection.displayName = 'BalanceTokenUsageSection'
